<script setup lang="ts">
import { watch, onMounted, onUnmounted, computed } from 'vue'
import { storeToRefs } from 'pinia'
import NavBar from '@/components/NavBar.vue'
import TabHistory from '@/components/TabHistory.vue'
import TabSms from '@/components/TabSms.vue'
import LoginScreen from '@/components/LoginScreen.vue'
import LeadInfo from '@/components/LeadInfo.vue'
import ConnectionIndicator from '@/components/ConnectionIndicator.vue'
import VolumeMeters from '@/components/VolumeMeters.vue'
import BottomNav from '@/components/BottomNav.vue'
import CallConfirmModal from '@/components/CallConfirmModal.vue'
import DialpadPanel from '@/components/DialpadPanel.vue'
import TransferPanel from '@/components/TransferPanel.vue'
import CallControls from '@/components/CallControls.vue'
import DispositionScreen from '@/components/DispositionScreen.vue'
import TabNewCall from '@/components/TabNewCall.vue'
import { useCall } from '@/composables/useCall'
import { useUserStore } from '@/stores/user'
import { useSmsStore } from '@/stores/sms'
import { usePusherStore } from '@/stores/pusher'
import { useStatusStore } from '@/stores/status'
import { useOnlineStore } from '@/stores/online'
import { useRegisteredStore } from '@/stores/registered'
import * as Sentry from '@sentry/vue'
import 'vue-sonner/style.css'
import { Toaster } from 'vue-sonner'
import backgroundUrl from './assets/background.png'

const userStore = useUserStore()
const { user } = storeToRefs(userStore)

const canAccessSms = computed(() => {
  if (!user.value) return false
  const role = user.value.role

  if (role === 'admin' || role === 'owner') {
    return true
  } else if (role === 'agent') {
    return user.value.account.is_agent_sms
  } else if (role === 'manager') {
    return user.value.account.is_manager_sms
  }

  return false
})

const statusStore = useStatusStore()
const { status } = storeToRefs(statusStore)

const pusherStore = usePusherStore()

const onlineStore = useOnlineStore()
const { online } = storeToRefs(onlineStore)

const registeredStore = useRegisteredStore()
const { registered } = storeToRefs(registeredStore)

const smsStore = useSmsStore()

// The call engine (Twilio device lifecycle, call state, disposition, etc.)
// lives in the useCall composable; App.vue orchestrates it and renders the UI.
const {
  call,
  call_user,
  error_message,
  message,
  note,
  lead,
  isMuted,
  isOnHold,
  isDialpadOpen,
  isTransferOpen,
  isTransferStarted,
  isMakingCall,
  openTab,
  showCallConfirm,
  callConfirmLead,
  autoaccept,
  dispositionCountdown,
  dispositionTimer,
  DISPOSITION_SECONDS_TO_WAIT,
  getCallerIds,
  getAgents,
  startPingInterval,
  stopPingInterval,
  startPhone,
  getAudioPermissions,
  reset,
  handleOnlineStatusChange,
  disconnect,
  acceptCall,
  reject,
  hangup,
  mute,
  unmute,
  digitPress,
  goHome,
  toggleHistory,
  toggleSms,
  toggleNewCall,
  confirmCallLead,
  cancelCallConfirm,
  confirmAndCall,
  toggleDialpad,
  toggleTransfer,
  makeOutboundCall,
  startTransfer,
  hold,
  unhold,
  thankyou,
  disposition,
  saveNote,
  skipNote,
  retry,
  handleDoneClick,
} = useCall()

watch(
  [user, registered],
  ([newUser, newRegistered]) => {
    if (newUser?.id && newRegistered) {
      startPingInterval()
    } else {
      stopPingInterval()
    }
  },
  { immediate: true },
)

// Keep the SMS store's tabOpen flag in sync with whether the SMS panel is
// actually visible, so incoming messages are only auto-marked-read while the
// user is viewing SMS — regardless of how they leave it (other nav tab, Home,
// or an incoming call auto-closing panels).
watch(
  openTab,
  (tab) => {
    smsStore.tabOpen = tab === 'sms'
  },
  { flush: 'sync' },
)

// Watch for disposition status to start countdown timer
watch(status, (newStatus) => {
  // Clear any existing disposition timer
  if (dispositionTimer.value) {
    clearInterval(dispositionTimer.value)
    dispositionTimer.value = null
  }

  // Start countdown when entering disposition screen
  if (newStatus === 'disposition') {
    dispositionCountdown.value = DISPOSITION_SECONDS_TO_WAIT
    dispositionTimer.value = window.setInterval(() => {
      dispositionCountdown.value--
      if (dispositionCountdown.value <= 0) {
        if (dispositionTimer.value) {
          clearInterval(dispositionTimer.value)
          dispositionTimer.value = null
        }
        reset()
      }
    }, 1000)
  }
})

// Lifecycle hooks
onMounted(async () => {
  await userStore.getUser()

  if (!userStore.user?.id) {
    return
  }

  Sentry.setUser({
    id: userStore.user.id,
    email: userStore.user.email,
  })

  await getAudioPermissions()
  getCallerIds()
  getAgents()
  startPhone()

  const tel = new URLSearchParams(window.location.search).get('tel')
  if (tel && tel.startsWith('tel:')) {
    const parsedTel = tel.replace(/[^+0-9]/g, '')
    toggleNewCall(undefined, parsedTel)
  }

  pusherStore.init()

  // Fetch SMS unread count
  smsStore.fetchUnreadCount()

  window.addEventListener('online', handleOnlineStatusChange)
  window.addEventListener('offline', handleOnlineStatusChange)
  window.addEventListener('beforeunload', disconnect)
})

onUnmounted(() => {
  window.removeEventListener('online', handleOnlineStatusChange)
  window.removeEventListener('offline', handleOnlineStatusChange)
  window.removeEventListener('beforeunload', disconnect)
  stopPingInterval()
  disconnect()
})
</script>

<template>
  <main class="app-shell">
    <Toaster richColors position="top-center" closeButtonPosition="top-right" closeButton />
    <NavBar v-model:autoaccept="autoaccept" />

    <div
      :style="{ backgroundImage: `url(${backgroundUrl})` }"
      class="bg-cover h-[calc(100%-70px-env(safe-area-inset-top,0px))] pt-[20px] overflow-y-auto [-webkit-overflow-scrolling:touch] max-[449px]:pb-[calc(60px+env(safe-area-inset-bottom,0px))]"
    >
      <LoginScreen v-if="status === 'login'" />

      <div v-if="user?.id">
        <div class="text-center" v-if="!isDialpadOpen && !isTransferOpen && !openTab">
          <LeadInfo :lead="lead" :call="call" :message="message" />
        </div>

        <ConnectionIndicator v-if="!openTab" />

        <CallControls
          :status="status"
          :call="call"
          :call-user="call_user"
          :is-muted="isMuted"
          :is-on-hold="isOnHold"
          :is-transfer-started="isTransferStarted"
          @accept="acceptCall"
          @reject="reject"
          @hold="hold"
          @unhold="unhold"
          @transfer="toggleTransfer"
          @dialpad="toggleDialpad"
          @mute="mute"
          @unmute="unmute"
          @hangup="hangup"
        />

        <DispositionScreen
          :status="status"
          :disposition-countdown="dispositionCountdown"
          :call="call"
          :is-dialpad-open="isDialpadOpen"
          :open-tab="openTab"
          :can-retry="!!user?.permissions.includes('app-retry')"
          v-model:note="note"
          @disposition="disposition"
          @retry="retry"
          @skip-note="skipNote"
          @save-note="saveNote"
          @thankyou="thankyou"
          @add-note="status = 'note'"
          @done="handleDoneClick"
        />

        <DialpadPanel v-if="isDialpadOpen" @digit="digitPress" />

        <TransferPanel v-if="isTransferOpen" @transfer="startTransfer" />

        <div v-if="openTab == 'history'">
          <TabHistory @new-call="confirmCallLead" />
        </div>

        <div v-if="canAccessSms && openTab == 'sms'">
          <TabSms @call="confirmCallLead" />
        </div>

        <div v-show="openTab == 'new_call'">
          <TabNewCall
            :error-message="error_message"
            :disabled="isMakingCall || !online || !registered"
            @submit="makeOutboundCall"
          />
        </div>
      </div>
    </div>

    <div
      v-if="user?.id && !isTransferOpen && !openTab"
      class="absolute bottom-[calc(10px+env(safe-area-inset-bottom,0px))] right-0 left-0"
    >
      <VolumeMeters />
    </div>

    <BottomNav
      v-if="user && status == 'waiting'"
      :can-access-sms="canAccessSms"
      @home="goHome"
      @call="toggleNewCall"
      @sms="toggleSms"
      @history="toggleHistory"
    />

    <CallConfirmModal
      :show="showCallConfirm"
      :lead="callConfirmLead"
      :error-message="error_message"
      :is-making-call="isMakingCall"
      @cancel="cancelCallConfirm"
      @confirm="confirmAndCall"
    />
  </main>
</template>

<style scoped>
/*
 * Component styling is expressed as Tailwind utilities in the templates.
 * What remains here is the irreducible responsive shell plus one descendant
 * rule that a utility on a single element cannot express.
 */
.app-shell {
  /* Viewport height, with a vh fallback for browsers without dvh. A var()
     can't fall back the way two declarations of the same property do, so the
     dvh upgrade is applied through @supports below. */
  --shell-vh: 100vh;
  /* The shell's own height, published for descendants that have to fit inside
     it (the NavBar dropdown) rather than restating these rules. */
  --shell-h: var(--shell-vh);
  position: relative;
  width: 100%;
  max-width: 450px;
  height: var(--shell-h);
  margin: auto;
  overflow: hidden;
}

@supports (height: 100dvh) {
  .app-shell {
    --shell-vh: 100dvh;
  }
}

/* Compact popup height on desktop; phones get the full viewport */
@media (min-width: 450px) {
  .app-shell {
    --shell-h: min(var(--shell-vh), 622px);
  }
}

/* iOS Safari zooms into focused fields under 16px. :deep() targets form
   controls inside child components, which a container utility can't reach. */
@media (max-width: 449px) {
  :deep(input),
  :deep(select),
  :deep(textarea) {
    font-size: 16px;
  }
}

body {
  color: #073b4c;
  overflow: hidden;
}
</style>
